import { useCallback, useMemo } from "react";
import { v4 as uuid } from "uuid";

import { Team, useCreateTeamMutation, useGetAllTeamsQuery } from "../../../redux/slices";
import { SportPlay } from "../../../types";
import { TSelectItem, useSelect } from "../../select";
import { UseSelect } from "../types";

export type SelectTeamItem = TSelectItem<Team>;
export const useSelectTeam = <TPlay extends SportPlay>({ editingPlay, updatePlay }: UseSelect<TPlay>) => {
  const { data: teams } = useGetAllTeamsQuery();

  const teamItems: SelectTeamItem[] = useMemo(
    (): SelectTeamItem[] => teams?.map((t) => ({ ...t, label: t.name })) || [],
    [teams]
  );

  const selectedTeam: SelectTeamItem | undefined = useMemo(
    () => (editingPlay.team ? { ...editingPlay.team, label: editingPlay.team.name } : undefined),
    [editingPlay]
  );

  const handleChangeTeam = (item: SelectTeamItem) => {
    updatePlay((prevValue) => ({ ...prevValue, team: item }));
  };

  const [createTeam] = useCreateTeamMutation();
  const handleNewTeam = (query: string): TSelectItem<Team> => {
    const id = uuid();
    return {
      id,
      team_id: id,
      label: query,
      name: query,
      nick: "",
      colorPrimary: "",
      conference: "",
      isEnterpriseTeam: false,
      imageUrl: "",
      noOfPlays: 0,
    };
  };

  const createNewTeam = useCallback(
    async (item: TSelectItem<Team>) => {
      const oldTeam = editingPlay.team;

      try {
        updatePlay((prevValue) => ({ ...prevValue, team: item }));
        const team = await createTeam({ name: item.name }).unwrap();
        updatePlay((prevValue) => ({ ...prevValue, team: team.data }));
      } catch (err) {
        console.error(err);
        updatePlay((prevValue) => ({ ...prevValue, team: oldTeam }));
      }
    },
    [createTeam, updatePlay, editingPlay]
  );

  const teamSelect = useSelect({
    items: teamItems,
    initialSelectedItem: selectedTeam,
    onChange: handleChangeTeam,
    createItem: createNewTeam,
  });

  return { teamSelect, handleNewTeam };
};
